// ═══════════════════════════════════════════════════════════════════════════════
// Single inventory row: toggle checklist membership, remove from list, rename item
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { ApiClientError, inventoryApi } from '../../lib/api';
import { mapApiClientErrorToMessage } from '../../i18n/mapApiClientError';
import { AppButton } from '../ui/AppButton';
import { AppModal } from '../ui/AppModal';
import { ConfirmDialog } from '../ui/ConfirmDialog';

export type ChecklistItemRow = { id: string; name: string };

type InventoryChecklistItemRowProps = {
  item: ChecklistItemRow;
  /** Prefix for `id` / `htmlFor` to avoid duplicate ids on the same page. */
  idPrefix: string;
  checked: boolean;
  /** Show remove / require list to be selected. */
  listActionsEnabled: boolean;
  isRowBusy: boolean;
  onToggle: () => void;
  /** Persist removing this item from the list on the server. Resolves to true when saved. */
  onRemoveFromList: () => Promise<boolean>;
  onInventoryChanged: () => void | Promise<void>;
};

const PencilIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
    />
  </svg>
);

const TrashIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
);

export const InventoryChecklistItemRow = (props: InventoryChecklistItemRowProps) => {
  const { t } = useLanguage();
  const { item, idPrefix, checked, listActionsEnabled, isRowBusy, onToggle, onRemoveFromList, onInventoryChanged } =
    props;

  const [renameOpen, setRenameOpen] = useState(false);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [removeConfirmLoading, setRemoveConfirmLoading] = useState(false);
  const [renameValue, setRenameValue] = useState(item.name);
  const [renameSaving, setRenameSaving] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);

  const openRename = useCallback(() => {
    setRenameValue(item.name);
    setRenameError(null);
    setRenameOpen(true);
  }, [item.name]);

  const closeRename = useCallback(() => {
    setRenameOpen(false);
    setRenameError(null);
  }, []);

  const openRemoveConfirm = useCallback(() => {
    if (isRowBusy) {
      return;
    }
    setRemoveConfirmOpen(true);
  }, [isRowBusy]);

  const closeRemoveConfirm = useCallback(() => {
    if (removeConfirmLoading) {
      return;
    }
    setRemoveConfirmOpen(false);
  }, [removeConfirmLoading]);

  const handleRemoveConfirm = useCallback(async () => {
    if (isRowBusy) {
      return;
    }
    setRemoveConfirmLoading(true);
    try {
      const ok = await onRemoveFromList();
      if (ok) {
        setRemoveConfirmOpen(false);
      }
    } finally {
      setRemoveConfirmLoading(false);
    }
  }, [isRowBusy, onRemoveFromList]);

  const handleRenameSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const name = renameValue.trim();
      if (name === '') {
        setRenameError(t('task.inventory.itemNameRequired'));
        return;
      }
      if (name === item.name) {
        closeRename();
        return;
      }
      setRenameSaving(true);
      setRenameError(null);
      try {
        const res = await inventoryApi.updateItem(item.id, { name });
        if (!res.success) {
          setRenameError(res.error ?? t('task.inventory.updateItemError'));
          return;
        }
        closeRename();
        await onInventoryChanged();
      } catch (caught) {
        setRenameError(
          caught instanceof ApiClientError
            ? mapApiClientErrorToMessage(caught, t) ?? t('task.inventory.updateItemError')
            : t('task.inventory.updateItemError'),
        );
      } finally {
        setRenameSaving(false);
      }
    },
    [closeRename, item.id, item.name, onInventoryChanged, renameValue, t],
  );

  const checkId = `${idPrefix}-pick-${item.id}`;

  return (
    <li className="rounded-md border border-transparent py-1" role="listitem" aria-busy={isRowBusy}>
      {/* Opacity on row content only: overlays (ConfirmDialog / rename) are siblings so they stay fully opaque. */}
      <div
        className={`flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3 transition-opacity ${
          removeConfirmOpen || renameOpen ? 'opacity-80' : ''
        }`}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <input
            type="checkbox"
            id={checkId}
            checked={checked}
            onChange={onToggle}
            disabled={isRowBusy}
            className="h-4 w-4 shrink-0 rounded border-gray-300"
          />
          <label htmlFor={checkId} className="min-w-0 flex-1 text-sm text-gray-900 dark:text-gray-100">
            {item.name}
          </label>
        </div>
        {listActionsEnabled ? (
          <div className="flex shrink-0 items-center gap-1.5 pl-6 sm:pl-0 sm:pr-0">
            <button
              type="button"
              onClick={openRename}
              disabled={isRowBusy}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-800 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
              title={t('task.inventory.renameItemCta')}
              aria-label={t('task.inventory.renameItemCta')}
            >
              <PencilIcon />
            </button>
            <button
              type="button"
              onClick={openRemoveConfirm}
              disabled={isRowBusy}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100 focus:outline-none focus:ring-2 focus:ring-rose-400 dark:border-rose-900/60 dark:bg-rose-950/50 dark:text-rose-100 dark:hover:bg-rose-950/80"
              title={t('task.inventory.removeFromListCta')}
              aria-label={t('task.inventory.removeFromListCta')}
            >
              <TrashIcon />
            </button>
          </div>
        ) : null}
      </div>

      <ConfirmDialog
        open={removeConfirmOpen}
        title={t('task.inventory.removeFromListConfirmTitle')}
        description={t('task.inventory.removeFromListConfirmBody')}
        confirmLabel={removeConfirmLoading ? t('common.loading') : t('task.inventory.removeFromListConfirmCta')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void handleRemoveConfirm()}
        onCancel={closeRemoveConfirm}
        variant="danger"
        confirmDisabled={removeConfirmLoading}
        cancelDisabled={removeConfirmLoading}
      />

      <AppModal
        open={renameOpen}
        onClose={closeRename}
        title={t('task.inventory.renameItemModalTitle')}
        titleId={`${idPrefix}-rename-title-${item.id}`}
        closeAriaLabel={t('common.cancel')}
      >
        <form onSubmit={(e) => void handleRenameSubmit(e)}>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200" htmlFor={`${idPrefix}-rename-input-${item.id}`}>
            {t('task.inventory.itemNameLabel')}
          </label>
          <input
            id={`${idPrefix}-rename-input-${item.id}`}
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            className="mt-1 w-full min-h-11 rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            maxLength={200}
            autoComplete="off"
            autoFocus
          />
          {renameError ? (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400" role="alert">
              {renameError}
            </p>
          ) : null}
          <div className="mt-4 flex justify-end gap-2">
            <AppButton type="button" variant="ghost" onClick={closeRename}>
              {t('common.cancel')}
            </AppButton>
            <AppButton type="submit" variant="primary" disabled={renameSaving}>
              {renameSaving ? t('common.loading') : t('task.inventory.renameItemSave')}
            </AppButton>
          </div>
        </form>
      </AppModal>
    </li>
  );
};
